import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import discoveryService from '../../services/discoveryService';
import { setActiveTenantSlug } from '../../services/api';
import type { DiscoveredLeague } from '@sulis-liiga/common';

const SELECTED_LEAGUE_KEY = 'sulis_selectedLeagueId';
const SELECTED_TENANT_KEY = 'sulis_selectedTenantSlug';

// Define the state interface
interface UIState {
  sidebarOpen: boolean;
  modalOpen: boolean;
  modalContent: string | null;
  alerts: Array<{
    id: string;
    type: 'success' | 'error' | 'info' | 'warning';
    message: string;
  }>;
  theme: 'light' | 'dark';
  // Selected league for the banner
  selectedLeagueId: string | null;
  /** Tenant slug that owns the selected league — drives the axios path-prefix rewrite. */
  selectedTenantSlug: string | null;
  bannerLeagues: DiscoveredLeague[];
  bannerLoading: boolean;
  /**
   * True once the cross-tenant discovery fetch has SETTLED at least once
   * (fulfilled or rejected). Distinguishes "discovery hasn't started yet"
   * (the initial state) from "discovery finished and returned no leagues" —
   * so tenant resolution never early-resolves with an empty slug on cold load.
   */
  bannerLoaded: boolean;
}

// Initial state — restore selectedLeagueId / selectedTenantSlug from localStorage if present.
// (localStorage is unavailable in node/test environments — guard with typeof)
const storedLeagueId =
  typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_LEAGUE_KEY) : null;
const storedTenantSlug =
  typeof localStorage !== 'undefined' ? localStorage.getItem(SELECTED_TENANT_KEY) : null;

// Prime the interceptor with whatever was persisted so it is ready before
// the discovery thunk completes (e.g. on a hard reload).
if (storedTenantSlug) {
  setActiveTenantSlug(storedTenantSlug);
}

const initialState: UIState = {
  sidebarOpen: false,
  modalOpen: false,
  modalContent: null,
  alerts: [],
  theme: 'light',
  selectedLeagueId: storedLeagueId,
  selectedTenantSlug: storedTenantSlug,
  bannerLeagues: [],
  bannerLoading: false,
  bannerLoaded: false,
};

// Thunk: load all leagues for the banner via the cross-tenant discovery endpoint.
// Public, no auth required — fires on app start regardless of login state.
export const fetchBannerLeagues = createAsyncThunk(
  'ui/fetchBannerLeagues',
  async (_, { getState, dispatch }) => {
    const leagues: DiscoveredLeague[] = await discoveryService.getLeagues();

    // Apply default selection precedence:
    // 1. last-selected (already in state)  2. Oulu  3. first available
    const state = getState() as { ui: UIState };
    const currentId = state.ui.selectedLeagueId;
    const stillExists = leagues.some(l => l.leagueId === currentId);

    if (!stillExists) {
      const oulu = leagues.find(l => l.name === 'Oulu');
      const fallback = oulu ?? leagues[0] ?? null;
      if (fallback) {
        dispatch(
          setSelectedLeague({ leagueId: fallback.leagueId, tenantSlug: fallback.tenantSlug }),
        );
      }
    } else {
      // Persisted selection still valid — ensure the tenant slug is up-to-date
      // (the discovery response is the source of truth for tenantSlug).
      const current = leagues.find(l => l.leagueId === currentId);
      if (current && current.tenantSlug !== state.ui.selectedTenantSlug) {
        dispatch(setSelectedLeague({ leagueId: current.leagueId, tenantSlug: current.tenantSlug }));
      } else if (current) {
        // Slug unchanged — just make sure the interceptor is primed.
        setActiveTenantSlug(current.tenantSlug);
      }
    }

    return leagues;
  },
);

// Create the UI slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: state => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    openModal: (state, action: PayloadAction<string>) => {
      state.modalOpen = true;
      state.modalContent = action.payload;
    },
    closeModal: state => {
      state.modalOpen = false;
      state.modalContent = null;
    },
    addAlert: (
      state,
      action: PayloadAction<{
        type: 'success' | 'error' | 'info' | 'warning';
        message: string;
      }>,
    ) => {
      const newAlert = {
        id: Date.now().toString(),
        type: action.payload.type,
        message: action.payload.message,
      };
      state.alerts.push(newAlert);
    },
    removeAlert: (state, action: PayloadAction<string>) => {
      state.alerts = state.alerts.filter(alert => alert.id !== action.payload);
    },
    clearAlerts: state => {
      state.alerts = [];
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('theme', action.payload);
      }
    },
    /**
     * Select a league by its id and the owning tenant slug.
     * Persists both to localStorage and primes the axios interceptor.
     */
    setSelectedLeague: (state, action: PayloadAction<{ leagueId: string; tenantSlug: string }>) => {
      const { leagueId, tenantSlug } = action.payload;
      state.selectedLeagueId = leagueId;
      state.selectedTenantSlug = tenantSlug;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(SELECTED_LEAGUE_KEY, leagueId);
        localStorage.setItem(SELECTED_TENANT_KEY, tenantSlug);
      }
      // Mirror into the api module so the interceptor reads it synchronously.
      setActiveTenantSlug(tenantSlug);
    },
    /**
     * Legacy compat: select by leagueId only (no tenant slug known yet).
     * Kept so any code that only knows the leagueId can still dispatch this.
     * The tenant slug will be reconciled on the next fetchBannerLeagues call.
     */
    setSelectedLeagueId: (state, action: PayloadAction<string>) => {
      state.selectedLeagueId = action.payload;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(SELECTED_LEAGUE_KEY, action.payload);
      }
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchBannerLeagues.pending, state => {
      state.bannerLoading = true;
    });
    builder.addCase(fetchBannerLeagues.fulfilled, (state, action) => {
      state.bannerLoading = false;
      state.bannerLoaded = true;
      state.bannerLeagues = action.payload;
    });
    builder.addCase(fetchBannerLeagues.rejected, state => {
      state.bannerLoading = false;
      state.bannerLoaded = true;
    });
  },
});

export const {
  toggleSidebar,
  setSidebarOpen,
  openModal,
  closeModal,
  addAlert,
  removeAlert,
  clearAlerts,
  setTheme,
  setSelectedLeague,
  setSelectedLeagueId,
} = uiSlice.actions;

export default uiSlice.reducer;
